import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { BasicContainer } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { DesignSystemTheme } from '@minecraft.themes';
import type { IconSize } from '../Icon/Icon';
import { MediaVideoProps } from './types';

const FALLBACK_POSTER_SVG =
  'data:image/svg+xml,%3Csvg viewBox="0 0 16 9" xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="16" height="9" fill="black"/%3E%3C/svg%3E';

export type MediaVideoThumbnailProps = Pick<MediaVideoProps, 'poster'> & {
  /** sets the width of the img element that renders the thumbnail
   * this does not change the actual width of the rendered image
   *
   * In general you should size the thumbnail responsively by
   * letting the thumbnail fill the width of the parent container
   * and the height set automatically to maintain the aspect ratio of the thumbnail
   * DO NOT USE width & height unless absolutely necessary
   *
   * if the width and height are not set to a 16:9 ratio then there will be
   * letterboxing on the sides of the image to maintain the aspect ratio of the thumbnail
   * you can control the color of the letterboxing with the bgColor prop, default is transparent
   */
  width?: number;
  /** sets the height of the img element that renders the thumbnail
   * this does not change the actual height of the rendered image
   *
   * In general you should size the thumbnail responsively by
   * letting the thumbnail fill the width of the parent container
   * and the height set automatically to maintain the aspect ratio of the thumbnail
   * DO NOT USE width & height unless absolutely necessary
   *
   * if the width and height are not set to a 16:9 ratio then there will be
   * letterboxing on the sides of the image to maintain the aspect ratio of the thumbnail
   * you can control the color of the letterboxing with the bgColor prop, default is transparent
   */
  height?: number;
  /** Alt Text (usually the name of the media file) */
  alt: string;
  /** Sets the `data-qa-id` attribute (Playwright selector). */
  'data-qa-id'?: string;
  /** Sets the `data-testid` attribute (Jest selector). */
  'data-testid'?: string;
  /** sets the background color of the video element if the video does not fill it completely */
  bgColor?: keyof DesignSystemTheme['bgColors'];
  /** Show the play icon overlay when controls are hidden, if controls = true then this is ignored */
  showPlayIcon?: boolean;
  playIconSize?: IconSize;
};

/**
 * `MediaVideoThumbnail` — image element that renders the "poster" frame of a video
 *
 * Use this when rendering video content but when it's not playable by the user
 * and we only need to show the "representative" frame of the video.
 */
export const MediaVideoThumbnail: React.FC<MediaVideoThumbnailProps> = ({
  poster,
  width,
  height,
  alt,
  'data-qa-id': qaId,
  'data-testid': testId,
  bgColor,
  showPlayIcon,
  playIconSize = 'md',
}) => {
  const [loadingErr, setLoadingErr] = useState(false);
  const theme = useTheme();
  return (
    <BasicContainer
      className="cn_relative"
      data-testid={testId || 'media-video-thumbnail'}
      {...(qaId ? { 'data-qa-id': qaId } : {})}
    >
      <img
        alt={alt}
        src={loadingErr ? FALLBACK_POSTER_SVG : poster}
        onError={() => setLoadingErr(true)}
        style={{
          backgroundColor: theme.designSystem.bgColors[bgColor ?? 'maxContrast'],
          width: typeof width === 'number' ? `${width}px` : '100%',
          height: typeof height === 'number' ? `${height}px` : 'auto',
          objectFit: 'contain',
        }}
        className="cn_w-full cn_aspect-16-9 cn_h-auto cn_inline-block"
      />

      {showPlayIcon && (
        <BasicContainer className="cn_center-absolute" data-testid="media-video-thumbnail-play-icon">
          <Icon name="play-circle-filled" size={playIconSize} />
        </BasicContainer>
      )}
    </BasicContainer>
  );
};
